import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  applyDefaultControlValuesToSource,
  applySpeakerControlValueMappingToControls,
  modifyCompositionProperty,
  resolveAndApplySpeakerForCompositionElement,
} from '@src/utility/compositionSettings';
import {
  getElementMetaObject,
  upsertElementMetaObject,
} from '@src/utility/extraElementData';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import AddIconWrapper from '../common/AddIconWrapper';
import { useCompositionContext } from './CompositionContext';
import {
  CompositionControl,
  type ControlSetting,
} from './CompositionSettings/CompositionControl';

import type { Speaker } from '@src/components/content-components/SpeakerTypes';
import type { ElementState } from '@src/renderer/ElementState';
// import SimpleLoadingSpinner from '../SimpleLoadingSpinner';

// Speaker utility orchestration moved into TranscriptSpeakerService
// Transcript element types handled internally by service

interface CompositionSettingsProps {
  activeElement: ElementState | null | undefined;
}

export interface Category {
  id: string;
  label: string;
  order: number;
}

export interface Subcategory {
  id: string;
  label: string;
  order: number;
  parentCategoryId: string;
}

export interface Composition {
  id: string;
  label: string;
  internal_name: string;
  aspect_ratio: string;
  source: Record<string, unknown>;
  controls: ControlSetting[]; // parsed array of control definitions
  thumbnail?: { url?: string };
}

const Icon = React.memo(
  ({
    style,
    stroke = '#848484',
    scale = 1,
  }: {
    style?: React.CSSProperties;
    stroke?: string;
    scale?: number;
  }) => {
    return (
      <IconWrapper width={14 * scale} height={14 * scale}>
        <svg
          width={`${6 * scale}px`}
          height={`${10 * scale}px`}
          viewBox={`0 0 ${6 * scale} ${10 * scale}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={style}
        >
          <path
            d="M1.25 0.625L5.625 5L1.25 9.375"
            stroke={stroke}
            strokeWidth={`${0.75 * scale}px`}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </IconWrapper>
    );
  },
);
Icon.displayName = 'Icon';

export const CompositionSettings = observer(
  (props: CompositionSettingsProps) => {
    const videoCreator = useVideoCreatorStore();
    const { editorStore } = useStore();
    const { activeElement } = props;

    // Get composition data from context
    const {
      subcategoryFilter,
      setSubcategoryFilter,
      selectedCategory,
      getSubcategoryById,
      getCompositionById,
    } = useCompositionContext();

    const { datoClientStore } = useStore();
    const scrollContainerRefs = useRef<{
      [key: string]: HTMLDivElement | null;
    }>({});
    const [scrollStates, setScrollStates] = useState<{
      [key: string]: { canScrollLeft: boolean; canScrollRight: boolean };
    }>({});

    // Get compositions for the selected subcategory (for "See All" view)
    const selectedSubcategory = useMemo(
      () =>
        subcategoryFilter
          ? getSubcategoryById(subcategoryFilter.subcategoryId)
          : null,
      [subcategoryFilter, getSubcategoryById],
    );

    const checkScrollPosition = useCallback((subcategoryId: string) => {
      const container = scrollContainerRefs.current[subcategoryId];
      if (!container) return;

      const canScrollLeft = container.scrollLeft > 0;
      const canScrollRight =
        container.scrollLeft <
        container.scrollWidth - container.clientWidth - 5;

      setScrollStates(prev => ({
        ...prev,
        [subcategoryId]: { canScrollLeft, canScrollRight },
      }));
    }, []);

    const resetScrollPositions = useCallback(() => {
      if (!selectedCategory) return;
      selectedCategory.subcategories.forEach(subcategory => {
        setScrollStates(prev => ({
          ...prev,
          [subcategory.id]: {
            canScrollLeft: false,
            canScrollRight: subcategory.compositions.length > 2,
          },
        }));
      });
    }, [selectedCategory]);

    const scrollSubcategory = useCallback(
      (subcategoryId: string, direction: 'left' | 'right') => {
        const container = scrollContainerRefs.current[subcategoryId];
        if (!container) return;

        const scrollAmount = 145 + 12; // Adjust based on card width + gap
        const targetScroll =
          direction === 'left'
            ? container.scrollLeft - scrollAmount
            : container.scrollLeft + scrollAmount;

        container.scrollTo({
          left: targetScroll,
          behavior: 'smooth',
        });

        // Update scroll state after animation
        setTimeout(() => checkScrollPosition(subcategoryId), 300);
      },
      [checkScrollPosition],
    );

    const createCompositionElement = async (composition: Composition) => {
      // Clone source to avoid mutating original composition definition
      const clonedSource: Record<string, unknown> = JSON.parse(
        JSON.stringify(composition.source || {}),
      );
      // Apply control-level default values directly into the cloned source tree
      applyDefaultControlValuesToSource({
        source: clonedSource,
        controls: composition.controls,
        organization: videoCreator.organization,
        story: videoCreator.story,
        convertToVh: v => videoCreator.textProducer.convertToVh(v),
      });

      const props = {
        time: videoCreator.time,
        templateId: composition.id,
        duration: 4,
        ...clonedSource,
      };

      const element = editorStore.editor.commandCreateCompositionElement(props);

      if (element && videoCreator.currentVideo) {
        // Associate composition template id with this element via utility (preserves other buckets)
        upsertElementMetaObject(videoCreator.currentVideo, element.id, {
          type: 'composition',
          templateId: composition.id,
          compositionControls: composition.controls,
        });

        setTimeout(async () => {
          editorStore.selectElement(element.id);
          await videoCreator.setActiveElements(element.id);
        }, 300);

        // Resolve speaker at current timeline time (best-effort) via service orchestrator
        // this needs to happen after the timeout above to ensure stores are populated

        await resolveAndApplySpeakerForCompositionElement({
          elementId: element.id,
          editorStore,
          datoClientStore,
          videoCreator,
        });
      }
    };

    const modifyProperty = (
      elementPaths: string[][],
      property: string,
      value: unknown,
    ) =>
      modifyCompositionProperty(
        activeElement?.source.id || '',
        editorStore,
        elementPaths,
        property,
        value,
        v => videoCreator.textProducer.convertToVh(v),
      );

    const renderCompositionSettings = () => {
      if (!activeElement) return null;

      const meta = getElementMetaObject(
        videoCreator.currentVideo,
        activeElement.source.id,
      );
      let templateId: string | undefined;
      if (meta && typeof meta === 'object') {
        for (const [k, v] of Object.entries(meta)) {
          if (k === 'templateId' && typeof v === 'string') {
            templateId = v;
            break;
          }
        }
      }
      if (!templateId) return null;

      const composition = getCompositionById(templateId);
      if (!composition) return null;

      const handleSpeakerControlChange = (
        inputMapping: Record<string, string>,
        speaker: Speaker,
      ) => {
        const plain: Record<string, unknown> = {};
        Object.entries(speaker).forEach(([k, v]) => {
          plain[k] = v;
        });

        applySpeakerControlValueMappingToControls(
          activeElement?.source.id || '',
          editorStore,
          composition.controls,
          inputMapping,
          plain,
        );

        upsertElementMetaObject(
          videoCreator.currentVideo,
          activeElement.source.id,
          { speakerLabel: plain.speakerLabel },
        );
      };

      return (
        <>
          <h2>Composition Settings</h2>
          {composition.controls.map((controlSetting, idx) => (
            <Item
              key={`${controlSetting.property || controlSetting.label}-${idx}`}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <CompositionControl
                control={controlSetting}
                activeElement={activeElement}
                modifyProperty={modifyProperty}
                handleSpeakerControlChange={handleSpeakerControlChange}
              />
            </Item>
          ))}
        </>
      );
    };

    // Helper function to render a single composition card
    const renderCompositionCard = useCallback(
      (composition: Composition) => (
        <AddIconWrapper
          key={composition.id}
          onClick={() => createCompositionElement(composition)}
          customStyles={{
            borderRadius: '10px',
            border: '2px solid transparent',
            transition: 'border-color 0.15s ease',
            overflow: 'hidden',
            padding: '0',
            display: 'inline-block',
            flexShrink: 0,
          }}
          customStylesWhenHovered={{
            borderColor: '#17C964',
          }}
        >
          <AddCompositionButton>
            {composition.thumbnail?.url ? (
              <ThumbnailImage
                src={composition.thumbnail.url}
                alt={composition.label}
                loading="lazy"
              />
            ) : (
              <PlaceholderContent>
                {composition.label} {composition.aspect_ratio}
              </PlaceholderContent>
            )}
          </AddCompositionButton>
        </AddIconWrapper>
      ),
      [createCompositionElement],
    );

    // Render all compositions for a specific subcategory (used in "See All" mode)
    const renderAllCompositionsForSubcategory = useCallback(() => {
      if (!subcategoryFilter || !selectedSubcategory) return null;
      return (
        <AllCompositionsForSubcategoryWrapper>
          <SubcategoryHeader>
            <SeeAllGoBackButton
              onClick={() => {
                setSubcategoryFilter(null);
              }}
            >
              <Icon style={{ transform: 'rotate(180deg)' }} />
              Back to Subcategories
            </SeeAllGoBackButton>
            <SubcategoryTitle>
              {subcategoryFilter.subcategoryLabel}
            </SubcategoryTitle>
          </SubcategoryHeader>
          <CompositionGridWrapper>
            {selectedSubcategory.compositions.map(renderCompositionCard)}
          </CompositionGridWrapper>
        </AllCompositionsForSubcategoryWrapper>
      );
    }, [
      subcategoryFilter,
      selectedSubcategory,
      setSubcategoryFilter,
      renderCompositionCard,
    ]);

    // Render the category tabs and subcategory carousels
    const renderCategoryView = useCallback(() => {
      if (!selectedCategory) return null;

      return (
        <AllCompositionsForSubcategoryWrapper>
          {/* Subcategories with horizontal carousels */}
          <SubcategoriesWrapper>
            {selectedCategory.subcategories.map(subcategory => {
              if (subcategory.compositions.length === 0) return null;

              const scrollState = scrollStates[subcategory.id] || {
                canScrollLeft: false,
                canScrollRight: false,
              };

              return (
                <SubcategorySection key={subcategory.id}>
                  <SubcategoryHeader>
                    <SubcategoryTitle>{subcategory.label}</SubcategoryTitle>
                    <SeeAllGoBackButton
                      onClick={() => {
                        setSubcategoryFilter({
                          subcategoryId: subcategory.id,
                          subcategoryLabel: subcategory.label,
                        });
                        resetScrollPositions();
                      }}
                    >
                      See All
                      <Icon />
                    </SeeAllGoBackButton>
                  </SubcategoryHeader>
                  <HorizontalScrollWrapper>
                    {scrollState.canScrollLeft && (
                      <NextPrevButton
                        position="left"
                        onClick={() =>
                          scrollSubcategory(subcategory.id, 'left')
                        }
                      >
                        <Icon
                          scale={1.6}
                          stroke={'#17C964'}
                          style={{
                            transform: 'translate(-10%, -15%) rotate(180deg)',
                          }}
                        />
                      </NextPrevButton>
                    )}
                    <HorizontalScrollContent
                      ref={el => {
                        scrollContainerRefs.current[subcategory.id] = el;
                        if (el && !scrollStates[subcategory.id]) {
                          checkScrollPosition(subcategory.id);
                        }
                      }}
                      onScroll={() => checkScrollPosition(subcategory.id)}
                    >
                      {subcategory.compositions.map(renderCompositionCard)}
                    </HorizontalScrollContent>
                    {scrollState.canScrollRight && (
                      <NextPrevButton
                        position="right"
                        onClick={() =>
                          scrollSubcategory(subcategory.id, 'right')
                        }
                      >
                        <Icon
                          scale={1.6}
                          stroke={'#17C964'}
                          style={{ transform: 'translate(10%, 15%)' }}
                        />
                      </NextPrevButton>
                    )}
                  </HorizontalScrollWrapper>
                </SubcategorySection>
              );
            })}
          </SubcategoriesWrapper>
        </AllCompositionsForSubcategoryWrapper>
      );
    }, [
      selectedCategory,
      setSubcategoryFilter,
      scrollStates,
      scrollSubcategory,
      renderCompositionCard,
      checkScrollPosition,
      resetScrollPositions,
    ]);

    // if (isLoading) {
    //   return (
    // <Container style={{ width: '100%', height: '100%' }}>
    //   <SimpleLoadingSpinner
    //     text={null}
    //     icon={null}
    //     spinnerSize={16}
    //     spinnerWidth={3}
    //   />
    // </Container>);
    // }

    return (
      <div>
        {!activeElement && !subcategoryFilter && renderCategoryView()}
        {!activeElement &&
          subcategoryFilter &&
          renderAllCompositionsForSubcategory()}
        {activeElement && renderCompositionSettings()}
      </div>
    );
  },
);

// const Container = styled.div`
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   width: 100%;
//   overflow: hidden;
//   position: relative;
// `;

const AllCompositionsForSubcategoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 10px;
`;

// Subcategories
const SubcategoriesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SubcategorySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SubcategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SubcategoryTitle = styled.h3`
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  color: #848484;
`;

const SeeAllGoBackButton = styled.button`
  background: transparent;
  color: #848484;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 0px;
  transition: color 0.2s ease;
  display: inline-flex;

  svg {
    path {
      transition: stroke 0.2s ease;
      stroke: #848484;
    }
  }

  &:hover {
    color: #1ed46f;
    svg {
      path {
        stroke: #1ed46f;
      }
    }
  }
`;

const IconWrapper = styled.div<{ width?: number; height?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.width}px;
  height: ${props => props.height}px;
`;

// Horizontal Scroll
const HorizontalScrollWrapper = styled.div`
  position: relative;
  width: 100%;
  scroll-behavior: smooth;
`;

const HorizontalScrollContent = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: hidden;
`;

// Grid for "See All" view
const CompositionGridWrapper = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(145px, 1fr));
  gap: 8px;
  margin-bottom: 16px;
`;

const AddCompositionButton = styled.div`
  width: 145px;
  min-width: 145px;
  height: auto;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: 0.2s;
  background: #1a1a1a;
  display: block;

  &:hover {
    filter: brightness(1.1) saturate(1.1);
  }
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  display: block;
  object-fit: contain;
`;

const PlaceholderContent = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  text-align: center;
  font-size: 12px;
  color: #fff;
  background: #2a2a2a;
`;

const NextPrevButton = styled.button<{ position: 'left' | 'right' }>`
  ${props => (props.position === 'left' ? 'left: 0;' : 'right: 0;')}
  ${props =>
    props.position === 'left'
      ? 'transform: translate(-50%, -50%);'
      : 'transform: translate(50%, -50%);'}
  display: flex;
  position: absolute;
  top: 50%;
  border: none;
  background: #03041a;
  border-radius: 40px;
  height: 24px;
  width: 24px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
  &:hover {
    filter: brightness(1.1) saturate(1.1);
  }
  z-index: 10;
`;
